/**
 * src/shell/portal/components/SovereignScoreCard.tsx
 *
 * Public Sovereign Score Card (0–300).
 * Displays verification tier, reputation breakdown, DePIN relay credits,
 * and vouching status in a dark cybernetic card.
 */

import React, { useState } from 'react';
import {
  calculateSovereignScore,
  SovereignScoreProfile,
  SovereignScoreBreakdown,
} from '../../../domain/identity/sovereign-score';
import { Shield, Zap, Award, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

export interface SovereignScoreCardProps {
  profile: SovereignScoreProfile;
  displayName?: string;
  handle?: string;
  onVouchRequest?: () => void;
}

export const SovereignScoreCard: React.FC<SovereignScoreCardProps> = ({
  profile,
  displayName = 'Sovereign Peer',
  handle = 'anonymous.herx',
  onVouchRequest,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const breakdown: SovereignScoreBreakdown = calculateSovereignScore(profile);

  // Score percentage for progress gauge
  const scorePercent = Math.min(Math.round((breakdown.totalScore / 300) * 100), 100);

  const getTierColor = (score: number) => {
    if (score >= 250) return '#38bdf8'; // Sky
    if (score >= 180) return '#34d399'; // Emerald
    if (score >= 100) return '#f59e0b'; // Amber
    return '#94a3b8'; // Slate
  };

  const accentColor = getTierColor(breakdown.totalScore);

  return (
    <div
      style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 14,
        padding: 20,
        color: '#f8fafc',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        maxWidth: 420,
        margin: '0 auto',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>{breakdown.badgeFlair}</span>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{displayName}</span>
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>@{handle}</div>
        </div>
        <div
          style={{
            background: 'rgba(56, 189, 248, 0.1)',
            border: `1px solid ${accentColor}`,
            padding: '4px 10px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            color: accentColor,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Shield size={13} color={accentColor} />
          {breakdown.rankTitle}
        </div>
      </div>

      {/* Main Score Gauge */}
      <div
        style={{
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: 12,
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: '#64748b', textTransform: 'uppercase' }}>
            SOVEREIGN SCORE
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: accentColor }}>{breakdown.totalScore}</span>
            <span style={{ fontSize: 14, color: '#64748b', fontWeight: 600 }}>/ 300</span>
          </div>
        </div>

        {/* Circular indicator percentage */}
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: '50%',
            background: `radial-gradient(closest-side, #0f172a 79%, transparent 80% 100%), conic-gradient(${accentColor} ${scorePercent}%, #334155 0)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            color: '#f8fafc',
          }}
        >
          {scorePercent}%
        </div>
      </div>

      {/* Quick Status Pills */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
        <div
          style={{
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 12,
          }}
        >
          <div style={{ color: '#64748b', fontSize: 10, textTransform: 'uppercase', fontWeight: 700 }}>Verification</div>
          <div style={{ marginTop: 4, fontWeight: 600, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckCircle2 size={13} />
            {profile.verificationTier === 'vouched_3x' ? '3x Vouched' : profile.verificationTier}
          </div>
        </div>

        <div
          style={{
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 12,
          }}
        >
          <div style={{ color: '#64748b', fontSize: 10, textTransform: 'uppercase', fontWeight: 700 }}>Vouches Received</div>
          <div style={{ marginTop: 4, fontWeight: 600, color: '#34d399', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Zap size={13} />
            {profile.incomingVouches} peer{profile.incomingVouches === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      {/* Expandable Breakdown Toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          color: '#94a3b8',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '6px 0',
        }}
      >
        <span>{isExpanded ? 'Hide Metric Breakdown' : 'View Metric Breakdown'}</span>
        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Expanded Breakdown Rows */}
      {isExpanded && (
        <div style={{ marginTop: 12, borderTop: '1px solid #334155', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94a3b8' }}>Verification Level</span>
            <span style={{ fontWeight: 600, color: '#f8fafc' }}>+{breakdown.tierPoints} pts (max 100)</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94a3b8' }}>Content Endorsements</span>
            <span style={{ fontWeight: 600, color: '#f8fafc' }}>+{breakdown.qualityPoints} pts (max 50)</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94a3b8' }}>Peer Vouch Network</span>
            <span style={{ fontWeight: 600, color: '#f8fafc' }}>+{breakdown.vouchPoints} pts (max 50)</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94a3b8' }}>DePIN Bandwidth Relayed</span>
            <span style={{ fontWeight: 600, color: '#f8fafc' }}>+{breakdown.poprPoints} pts (max 50)</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94a3b8' }}>Jury Service Consensus</span>
            <span style={{ fontWeight: 600, color: '#f8fafc' }}>+{breakdown.juryPoints} pts (max 20)</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94a3b8' }}>Staking Longevity</span>
            <span style={{ fontWeight: 600, color: '#f8fafc' }}>+{breakdown.stakePoints} pts (max 30)</span>
          </div>
        </div>
      )}

      {/* Vouch CTA */}
      {onVouchRequest && profile.verificationTier !== 'vouched_3x' && (
        <button
          type="button"
          onClick={onVouchRequest}
          style={{
            width: '100%',
            marginTop: 16,
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            border: 'none',
            borderRadius: 8,
            color: '#ffffff',
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Award size={15} />
          Request Peer Vouches
        </button>
      )}
    </div>
  );
};
